// src/components/features/Interior/components/InteriorStep1PropertyType.jsx
// Step 1: Type of Property → Residential/Commercial
//         Residential → Bungalow/Flat + Number of Floors
//         Nature of Project → Renovation / New Build

import {
  Box, Flex, Text, Radio, Button, VStack,
  HStack,
} from "@chakra-ui/react";

const FLOORS_OPTIONS = [1, 2, 3, 4, 5, "6+"];

export default function InteriorStep1PropertyType({
  formData, setFormData, onNext, onBack, isLastStep, onSubmit, viewMode,
}) {
  const propertyType   = formData?.propertyType   || "";   // "residential" | "commercial"
  const residentialSub = formData?.residentialSub || "";   // "bungalow" | "flat"
  const numberOfFloors = formData?.interiorFloors  || "";
  const natureOfProject= formData?.natureOfProject || "";  // "renovation" | "new_build"

  const set = (key, val) => setFormData({ ...formData, [key]: val });

  const canProceed = propertyType === "commercial"
    ? !!natureOfProject
    : !!(residentialSub && numberOfFloors && natureOfProject);

  const handleNext = () => {
    if (!canProceed) return;
    if (isLastStep) onSubmit(); else onNext();
  };

  return (
    <Box
      h="600px" w="100%" maxW="400px" mx="auto"
      display="flex" flexDirection="column" justifyContent="space-between"
      px={4} py={6}
    >
      <Box
        flex="1" minH="0" overflowY="auto" overflowX="hidden" pr={1}
        pointerEvents={viewMode ? "none" : "auto"}
        opacity={viewMode ? 0.6 : 1}
        css={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" } }}
      >
        <VStack spacing={5} align="stretch">
          <Box>
            <Text fontSize="17px" fontWeight="600" color="gray.800">Type of Property</Text>
            <Text fontSize="12px" color="gray.500" mt={0.5}>Tell us about your project</Text>
          </Box>

          {/* Property Type */}
          <Box>
            <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>Property Type</Text>
            <VStack spacing={2} align="stretch">
              {[
                { id: "residential", label: "Residential", icon: "🏠" },
                { id: "commercial",  label: "Commercial",  icon: "🏢" },
              ].map((opt) => {
                const isSel = propertyType === opt.id;
                return (
                  <Box
                    key={opt.id}
                    border={isSel ? "2px solid #319795" : "1px solid #E2E8F0"}
                    borderRadius="10px" px={3} py={2} cursor="pointer"
                    bg={isSel ? "cyan.50" : "white"} transition="0.2s"
                    _hover={{ borderColor: "cyan.400" }}
                    onClick={() => set("propertyType", opt.id)}
                  >
                    <Flex align="center" gap={3}>
                      <Radio value={opt.id} colorScheme="cyan" isChecked={isSel} onChange={() => set("propertyType", opt.id)} />
                      <Text fontSize="14px">{opt.icon} {opt.label}</Text>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          </Box>

          {/* Residential sub-options */}
          {propertyType === "residential" && (
            <>
              <Box>
                <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>Property Sub-Type</Text>
                <HStack spacing={2}>
                  {[
                    { id: "bungalow", label: "🏡 Bungalow" },
                    { id: "flat",     label: "🏢 Flat / Apartment" },
                  ].map((opt) => {
                    const isSel = residentialSub === opt.id;
                    return (
                      <Box
                        key={opt.id}
                        flex="1"
                        border={isSel ? "2px solid #319795" : "1px solid #E2E8F0"}
                        borderRadius="10px" px={3} py={3} cursor="pointer" textAlign="center"
                        bg={isSel ? "cyan.50" : "white"} transition="0.2s"
                        _hover={{ borderColor: "cyan.400" }}
                        onClick={() => set("residentialSub", opt.id)}
                      >
                        <Text fontSize="13px" fontWeight={isSel ? "600" : "500"} color={isSel ? "cyan.700" : "gray.700"}>
                          {opt.label}
                        </Text>
                      </Box>
                    );
                  })}
                </HStack>
              </Box>

              <Box>
                <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>Number of Floors</Text>
                <Flex gap={2} flexWrap="wrap">
                  {FLOORS_OPTIONS.map((f) => {
                    const val  = String(f);
                    const isSel = numberOfFloors === val;
                    return (
                      <Box
                        key={val}
                        w="48px" h="40px"
                        border={isSel ? "2px solid #319795" : "1px solid #E2E8F0"}
                        borderRadius="8px" cursor="pointer" textAlign="center"
                        bg={isSel ? "cyan.50" : "white"}
                        display="flex" alignItems="center" justifyContent="center"
                        transition="0.2s" _hover={{ borderColor: "cyan.400" }}
                        onClick={() => set("interiorFloors", val)}
                      >
                        <Text fontSize="14px" fontWeight={isSel ? "700" : "500"} color={isSel ? "cyan.700" : "gray.700"}>
                          {f}
                        </Text>
                      </Box>
                    );
                  })}
                </Flex>
              </Box>
            </>
          )}

          {/* Nature of Project */}
          {propertyType && (
            <Box>
              <Text fontSize="13px" fontWeight="600" color="gray.600" mb={2}>Nature of Project</Text>
              <VStack spacing={2} align="stretch">
                {[
                  { id: "renovation", label: "🔨 Renovation",  sub: "Updating or remodeling existing interiors" },
                  { id: "new_build",  label: "✨ New Build",   sub: "Fresh interior fit-out for a new space" },
                ].map((opt) => {
                  const isSel = natureOfProject === opt.id;
                  return (
                    <Box
                      key={opt.id}
                      border={isSel ? "2px solid #319795" : "1px solid #E2E8F0"}
                      borderRadius="10px" px={3} py={2} cursor="pointer"
                      bg={isSel ? "cyan.50" : "white"} transition="0.2s"
                      _hover={{ borderColor: "cyan.400" }}
                      onClick={() => set("natureOfProject", opt.id)}
                    >
                      <Flex align="center" gap={3}>
                        <Radio value={opt.id} colorScheme="cyan" isChecked={isSel} onChange={() => set("natureOfProject", opt.id)} />
                        <Box>
                          <Text fontSize="14px" fontWeight="600" color="gray.800">{opt.label}</Text>
                          <Text fontSize="11px" color="gray.500">{opt.sub}</Text>
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>

      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button onClick={onBack} variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500" borderRadius="6px"
          minW="100px" h="40px" fontWeight="500" fontSize="14px" _hover={{ bg: "cyan.50" }}>
          ← Previous
        </Button>
        <Button onClick={handleNext} isDisabled={!canProceed} variant="solid"
          bg={canProceed ? "cyan.500" : "gray.300"} color="white"
          borderRadius="6px" minW="100px" h="40px" fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: canProceed ? "cyan.600" : "gray.300" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}>
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}